// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import ReportIcon from '@material-ui/icons/List';

import Graph from '../common/graph/Graph';
import ChartJs from '../common/graph/ChartJs';
import Table from '../common/table/Table';
import EmptyState from '../common/EmptyState';
import LoadingState from '../common/LoadingState';
import KPIBar from './KPIBar';

import Console from '../../lib/debug';

type Props = {
    columns: () => void, //required
    rows: () => void, //required
    includeGraph: boolean,
    disableTableFooter: boolean,
    KPIBar?: KPIBar,
    graphData?: {
        graphLibray: string,
        graphProps: object,
        xValues: Array,
        yValues: Array
    }
};

export class AnalyticsBaseClass extends Component<Props> {
    constructor(props: object) {
        super(props);
        if (!['c3', 'chart.js'].includes(props.graphLibrary)) {
            throw new Error('Invalid graphing library');
        }
    }

    getEmptyState = () => {
        if (
            this.props.analyticsLoading ||
            this.props.rows.length > 0 ||
            this.props.columns.length == 0
        ) {
            return null;
        }

        return (
            <EmptyState
                text="No Records Found"
                icon={<ReportIcon style={{ fontSize: 81 }} />}
            />
        );
    };

    getUnauthorizedState = () => {
        if (this.props.analyticsLoading) {
            return null;
        }

        return (
            <EmptyState
                title="Upgrade to access this feature"
                text="Sorry this feature isn't allowed with your current subscription."
                icon={<ReportIcon style={{ fontSize: 81 }} />}
            />
        );
    };

    getLoadingState = () => {
        if (!this.props.analyticsLoading) {
            return null;
        }

        return <LoadingState />;
    };

    getGraph = () => {
        const { includeGraph, graphData } = this.props;
        if (!includeGraph || !graphData) {
            return null;
        }

        const { graphProps, xValues, yValues } = this.props.graphData;
        const dataColumns = [xValues, yValues];
        const hasNoXValues = xValues.length <= 1;
        if (hasNoXValues) {
            return null;
        }

        return (
            <div id="chart" className="chart-c3">
                <Graph
                    dataColumns={dataColumns}
                    graphProps={graphProps}
                    x="xValues"
                />
            </div>
        );
    };

    getChart = () => {
        const { includeGraph, graphData } = this.props;

        if (
            !includeGraph ||
            !graphData ||
            !graphData.xValues.length ||
            !graphData.yValues.length
        ) {
            return null;
        }

        let graphProps = this.props.graphData.graphProps;

        const xValues = this.props.graphData.xValues;
        const yValues = this.props.graphData.yValues;
        const dataColumns = [xValues, yValues];
        const hasNoXValues = xValues.length < 1;

        if (hasNoXValues) {
            return null;
        }

        return (
            <div id="chart" className="chart-chart-js">
                <ChartJs dataColumns={dataColumns} graphProps={graphProps} />
            </div>
        );
    };

    getTable = () => {
        if (this.props.analyticsLoading || this.props.columns.length === 0) {
            return null;
        }

        return (
            <Table
                columns={this.props.columns}
                rows={this.props.rows}
                rowEvents={this.props.rowEvents}
                defaultSort={this.props.defaultSort}
            />
        );
    };

    getMetaDataDisplay = () => {
        let tableIsEmpty = !this.props.rows || this.props.rows.length === 0;
        if (this.props.analyticsLoading || tableIsEmpty) {
            return null;
        }
        let recordCount = this.props.rows.length;
        let displayContent = `Found ${recordCount} Record${
            recordCount > 1 ? 's' : ''
        }`;
        return <div className="table-meta-data-footer ">{displayContent}</div>;
    };

    render() {
        Console.render('AnalyticsBase');

        if (this.props.isBasic) {
            return this.getUnauthorizedState();
        }

        const graph =
            this.props.graphLibrary === 'c3'
                ? this.getGraph()
                : this.getChart();
        const KPIBar = this.props.KPIBar || null;
        const table = this.getTable();
        const emptyState = this.getEmptyState();
        const loadingState = this.getLoadingState();
        const resultsMetaDataFooter = this.getMetaDataDisplay();

        const className = this.props.includeKpi ? 'has-kpi' : '';
        return (
            <div className={className}>
                {graph}
                {KPIBar}
                {table}
                {emptyState}
                {loadingState}
                {!this.props.disableTableFooter ? resultsMetaDataFooter : null}
            </div>
        );
    }
}

const mapStateToProps = (state: any, ownProps: any) => {
    return {
        ...ownProps,
        analyticsLoading: state.app.analyticsLoading,
        columns: state.analytics.columns,
        rows: state.analytics.rows,
        defaultSort: state.analytics.defaultSort,
        graphData: state.analytics.graphData,
        isBasic: state.app.loggedInUser.isBasic
    };
};

export default connect(mapStateToProps)(AnalyticsBaseClass);

AnalyticsBaseClass.defaultProps = {
    graphData: {},
    graphLibrary: 'c3',
    KPIBar: null
};



// WEBPACK FOOTER //
// ./src/view/analytics/AnalyticsBase.jsx